import { warn } from "../core/config.js";
import { registerHooks } from "../core/hooks.js";
import type { SlaterSearchApi } from "../core/types.js";
import type { SlaterList } from "../list.js";

/**
 * The window bridge for per-project hooks. Three ways in, one registry:
 *
 *   1. `window.SlaterSearchConfig.hooks` — for scripts that ran before boot.
 *   2. A pre-boot command queue, drained here:
 *        window.SlaterSearch = window.SlaterSearch || { q: [] };
 *        window.SlaterSearch.q.push(["registerHooks", "site", { … }]);
 *   3. The live API this installs over the stub. Its `q.push` executes
 *      immediately (the gtag/dataLayer pattern), so the queue snippet above is
 *      the one canonical snippet — correct before and after boot. That matters
 *      because project JS (Slater-hosted files) usually loads after the embed.
 *
 * A post-boot registration re-runs the matching block(s) via `resetAndLoad()`
 * — usually a datasource cache hit, so no extra network. Boot calls this
 * BEFORE constructing any list: drain-time refreshes no-op against the still-
 * empty `lists` array, and the first load already sees the hooks.
 */
export function installHooksApi(lists: SlaterList[]): void {
  const configHooks = window.SlaterSearchConfig?.hooks;
  if (configHooks && typeof configHooks === "object") {
    for (const [key, hooks] of Object.entries(configHooks)) registerHooks(key, hooks);
  }

  const refresh = (key: string): void => {
    for (const list of lists) {
      if (key === "*" || list.config.key === key) list.resetAndLoad();
    }
  };

  const run = (command: unknown): void => {
    if (!Array.isArray(command)) {
      warn("hooks: malformed command — expected [name, ...args].", command);
      return;
    }
    const [name, key, hooks] = command as unknown[];
    if (name === "registerHooks") {
      if (registerHooks(key, hooks)) refresh(key as string);
    } else if (name === "refresh") {
      if (typeof key === "string") refresh(key);
      else warn("hooks: refresh needs a block key (or \"*\").");
    } else {
      warn(`hooks: unknown command "${String(name)}".`);
    }
  };

  // Drain whatever a pre-boot script queued on the stub.
  const existing = window.SlaterSearch;
  if (existing !== undefined) {
    const queued = (existing as { q?: unknown }).q;
    if (Array.isArray(queued)) {
      for (const command of queued) run(command);
    } else {
      warn("hooks: window.SlaterSearch is not the { q: [] } stub — replacing it.");
    }
  }

  const api: SlaterSearchApi = {
    loaded: true,
    registerHooks(key, hooks) {
      if (registerHooks(key, hooks)) refresh(key);
    },
    refresh,
    q: {
      push(...commands) {
        for (const command of commands) run(command);
      },
    },
  };
  window.SlaterSearch = api;
}
